import { useRouteLocale } from "@/hooks/useRouteLocale";
import { lazy, Suspense, useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { usePageMeta } from "@/hooks/usePageMeta";
import { USER_ROUTES } from "@/routes";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { UserHeader } from "@/components/customer/UserHeader";
import { getCookieConsent, hasPaymentCookieConsent, setCookieConsent } from "./CookieConsentBanner";

const PaymentSettings = lazy(() => import("@/pages/customer/PaymentSettings"));

/**
 * Renders the payment settings (Stripe Connect) route only when the user has accepted payment cookies.
 * PaymentSettings is lazy-loaded so @stripe/connect-js is never imported until consent is given.
 * When consent is missing, shows the same layout (UserHeader + content) with an inline consent card.
 */
export function PaymentSettingsRouteGate() {
  const { t } = useTranslation();
  const locale = useRouteLocale();
  const hasConsent = hasPaymentCookieConsent();
  const [, forceUpdate] = useState(0);
  const currentLocale = locale || "ro";

  usePageMeta({
    title: t("payments.stripe.connectedAccounts.metaTitle"),
    description: t("payments.stripe.connectedAccounts.description"),
    locale: currentLocale,
    path: USER_ROUTES.connectedAccounts(currentLocale),
    noindex: true,
  });

  const handleAccept = () => {
    const existing = getCookieConsent();
    setCookieConsent({
      essential: true,
      analytics: existing?.analytics ?? false,
      marketing: existing?.marketing ?? false,
      timestamp: Date.now(),
    });
    forceUpdate((n) => n + 1);
  };

  if (!hasConsent) {
    return (
      <div className="min-h-screen bg-background">
        <UserHeader currentLocale={currentLocale} active="events" />
        <main className="container mx-auto px-4 py-8 max-w-4xl min-h-[calc(100vh-4rem)]">
          <Card className="border-2">
            <CardHeader>
              <CardTitle>{t("payments.stripe.connectedAccounts.connectTitle")}</CardTitle>
              <CardDescription>
                {t("cookies.paymentConsent.connectMessage")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={handleAccept}>
                {t("cookies.paymentConsent.acceptAndContinue")}
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-background">
          <UserHeader currentLocale={currentLocale} active="events" />
          <div className="container mx-auto px-4 py-8 min-h-[calc(100vh-4rem)] flex items-center justify-center">
            <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
          </div>
        </div>
      }
    >
      <PaymentSettings />
    </Suspense>
  );
}
